import React from 'react';
import { BsBox, BsPencil } from 'react-icons/bs';
import {
    IoAddSharp,
    IoAlbumsOutline,
    IoBriefcaseOutline,
    IoCalendarOutline,
    IoCardOutline,
    IoChatbubblesOutline,
    IoChevronForward,
    IoCropOutline,
    IoHardwareChipOutline,
    IoHelpCircleOutline,
    IoMapOutline,
} from 'react-icons/io5';
import { TbHeartHandshake } from 'react-icons/tb';

import { ButtonProps } from '@mantine/core';
import { UserService } from 'src/frontend/services/UserService';
import useSWR from 'swr';

import { all as allComponentTypes, CompanyProfile, ComponentDefinition } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { ProjectHelpers } from 'helpers/ProjectHelpers';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { RecentProjects } from 'components/header/components/RecentProjects';
import { useHeaderState } from 'components/header/hooks/use-header-state';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { useCurrentTeamCompanies } from 'hooks/use-current-team-companies';
import { useCurrentUser } from 'hooks/use-current-user';
import { IndustryCategoriesWithProfileCounts, useIndustryCategories } from 'hooks/use-industry-categories';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import type { WorkspaceNavItem } from 'state/workspace-config';

export type NavItem = {
    title: string;
    href?: string;
    icon?: React.ReactNode;
    highlightPatterns?: RegExp[];
    showUpdateMessage?: boolean;
    items?: {
        icon?: React.ReactNode;
        title: string;
        href: string;
        highlighted?: boolean;
        external?: boolean;
        description?: string;
        hideInWorkspace?: boolean;
        children?: React.ReactNode;
        categories?: {
            key?: string;
            title: React.ReactNode;
            href?: string;
            hideOnMobile?: boolean;
            subcategories?: {
                icon?: React.ReactNode;
                title: string;
                href: string;
                external?: boolean;
                highlighted?: boolean;
            }[];
        }[];
    }[];

    bottomSection?: {
        title: string;
        description?: string;
        hideInWorkspace?: boolean;
        button: {
            label: string;
            href: string;
            target?: '_blank';
        } & ButtonProps;
    };
};

const createProductItem = (componentDefinition: ComponentDefinition) => ({
    title: componentDefinition.plural,
    href: ComponentHelpers.urls.search({ productsQuery: { type: componentDefinition.type } }),
});

const ADD_PRODUCT_BOTTOM_SECTION = (company?: CompanyProfile): NonNullable<NavItem['bottomSection']> => ({
    title: 'Showcase your products to industry professionals',
    description: 'Add your product in minutes',
    hideInWorkspace: true,
    button: {
        leftSection: <IoAddSharp size={16} />,
        label: 'Add Product',
        href: ComponentHelpers.urls.create({ company }),
    },
});

const PRODUCTS_ITEM: NavItem = {
    title: 'Find Products',
    highlightPatterns: [/^\/(products\/[^/]+)/],
    href: ComponentHelpers.urls.search(),
    icon: <BsBox size={16} />,
    items: [
        {
            icon: <IoBriefcaseOutline size={16} />,
            title: 'Find Manufacturers',
            // TODO hardcoded for now
            href: CompanyHelpers.urls.categories('manufacturers'),
            description: 'Discover trusted manufacturers',
            hideInWorkspace: true,
        },
        {
            icon: <BsBox size={16} />,
            title: 'Find Products',
            href: ComponentHelpers.urls.search(),
            description: 'Discover the building blocks for your next power project',
            categories: [
                {
                    title: 'Generation',
                    subcategories: [
                        createProductItem(allComponentTypes.solar),
                        createProductItem(allComponentTypes.wind),
                        createProductItem(allComponentTypes.hydro),
                        createProductItem(allComponentTypes.generator),
                        createProductItem(allComponentTypes.fuelCell),
                    ],
                    hideOnMobile: true,
                },
                {
                    title: 'Load',
                    subcategories: [
                        createProductItem(allComponentTypes.battery),
                        createProductItem(allComponentTypes.capacitor),
                        createProductItem(allComponentTypes.charger),
                        createProductItem(allComponentTypes.light),
                        createProductItem(allComponentTypes.hvac),
                        createProductItem(allComponentTypes.motor),
                    ],
                    hideOnMobile: true,
                },
                {
                    title: 'Protection',
                    subcategories: [
                        createProductItem(allComponentTypes.breaker),
                        createProductItem(allComponentTypes.contactor),
                        createProductItem(allComponentTypes.disconnect),
                        createProductItem(allComponentTypes.fuse),
                        createProductItem(allComponentTypes.rapidShutdownDevice),
                    ],
                    hideOnMobile: true,
                },
                {
                    title: 'Power Conversion',
                    subcategories: [
                        createProductItem(allComponentTypes.converter),
                        createProductItem(allComponentTypes.transformer),
                    ],
                    hideOnMobile: true,
                },
                {
                    title: 'Other',
                    subcategories: [
                        createProductItem(allComponentTypes.cable),
                        createProductItem(allComponentTypes.controller),
                        createProductItem(allComponentTypes.meter),
                        createProductItem(allComponentTypes.solution),
                    ],
                    hideOnMobile: true,
                },
            ],
        },
    ],
    bottomSection: {
        title: "Join the world's largest power industry community",
        description: 'Setup your profile in minutes',
        hideInWorkspace: true,
        button: {
            label: 'Join as a PRO',
            href: MarketingHelpers.urls.resourcepro.joinAsPro(),
        },
    },
};

const SERVICES_ITEM = (industryCategories: IndustryCategoriesWithProfileCounts): NavItem => ({
    title: 'Find Services',
    href: CompanyHelpers.urls.overview(),
    highlightPatterns: [/^\/(profiles\/[^/]+)/],
    icon: <IoBriefcaseOutline size={16} />,
    items: [
        {
            icon: <IoBriefcaseOutline size={16} />,
            title: 'Find Services',
            href: CompanyHelpers.urls.overview(),
            description: 'Discover and connect with trusted power industry professionals',
            categories: [
                ...industryCategories.slice(0, 6).map((category) => ({
                    title: category.name,
                    subcategories: category.subcategories.slice(0, 5).map((subcategory) => ({
                        title: subcategory.name,
                        href: CompanyHelpers.urls.categories(subcategory.slug),
                    })),
                    hideOnMobile: true,
                })),
                {
                    key: 'all-categories',
                    title: (
                        <>
                            All Categories
                            <IoChevronForward
                                style={{ marginLeft: '4px', marginBottom: '2px', verticalAlign: 'middle' }}
                            />
                        </>
                    ),
                    href: CompanyHelpers.urls.overview(),
                    hideOnMobile: true,
                },
            ],
        },
    ],
    bottomSection: {
        title: "Join the world's largest power industry community",
        description: 'Setup your profile in minutes',
        button: {
            label: 'Join as a PRO',
            href: MarketingHelpers.urls.resourcepro.joinAsPro(),
        },
    },
});

const useProductsItem = (): NavItem => {
    const user = useCurrentUser();
    const { companies } = useCurrentTeamCompanies();

    if (!user?.hasCompany) {
        return PRODUCTS_ITEM;
    }

    let company: CompanyProfile | undefined;

    if (companies.length === 1) {
        company = companies[0];
    }

    return {
        ...PRODUCTS_ITEM,
        bottomSection: ADD_PRODUCT_BOTTOM_SECTION(company),
    };
};

const toNavItem = (link: WorkspaceNavItem): NavItem => ({
    title: link.title,
    href: link.href,
    highlightPatterns: [
        new RegExp(`^${link.href}$`),
        ...(link.highlightPrefixes ?? []).map((prefix) => new RegExp(`^${prefix}`)),
    ],
    items: link.items,
});

export const useNavItems = (): NavItem[] => {
    const { showResourceProNav } = useHeaderState();
    const { workspace: workspaceActive, navLinks } = useWorkspaceConfig();

    const defaultNavItems = useDefaultNavItems();
    const resourceProNavItems = useResourceProNavItems();

    if (navLinks) {
        return navLinks.map(toNavItem);
    }

    const navItems = showResourceProNav ? resourceProNavItems : defaultNavItems;

    if (!workspaceActive) {
        return navItems;
    }

    return navItems.map((item) => ({
        ...item,
        items: item.items?.filter((subItem) => !subItem.hideInWorkspace),
        bottomSection: item.bottomSection?.hideInWorkspace ? undefined : item.bottomSection,
    }));
};

const useDefaultNavItems = (): NavItem[] => {
    const { data } = useSWR('/authenticated', UserService.heartbeat);
    const user = useCurrentUser();
    const authenticated = !!user || !!data?.authenticated;
    const { industryCategories } = useIndustryCategories();
    const productsItem = useProductsItem();

    return [
        {
            showUpdateMessage: true,
            highlightPatterns: [/^\/(projects\/[^/]+)/],
            title: 'Design Software',
            href: authenticated ? ProjectHelpers.urls.overview() : MarketingHelpers.urls.dcide.overview(),
            icon: <BsPencil size={16} />,
            items: [
                ...(authenticated
                    ? [
                          {
                              icon: <BsPencil size={16} />,
                              title: 'Your Designs',
                              description: 'Create and manage your designs',
                              href: ProjectHelpers.urls.overview(),
                              children: <RecentProjects />,
                          },
                      ]
                    : []),
                {
                    icon: <IoCropOutline size={16} />,
                    title: 'About DCIDE',
                    href: MarketingHelpers.urls.dcide.overview(),
                    description: 'Design single-line diagrams, share, collaborate, simulate',
                },
                {
                    icon: <IoChatbubblesOutline size={16} />,
                    title: 'Expert Support',
                    href: MarketingHelpers.urls.dcide.expertSupport(),
                    description:
                        "Get answers from engineers who've designed, simulated, and commissioned real DC power systems",
                },
                {
                    icon: <IoAlbumsOutline size={16} />,
                    title: 'Reference Designs',
                    href: RouterHelpers.urls.designs.search(),
                    description: 'Real-world examples to inspire your next project',
                },
                {
                    icon: <IoCardOutline size={16} />,
                    title: 'Pricing',
                    href: MarketingHelpers.urls.dcide.pricing(),
                    description: 'Discover how to go from Single-Line Diagram to Valid Bill of Materials',
                    hideInWorkspace: true,
                },
            ],
            bottomSection: {
                title: 'Design single-line diagrams, share, collaborate, simulate',
                description: 'Ultra-user-friendly, DC-first electrical design software',
                button: {
                    label: 'Learn More',
                    href: MarketingHelpers.urls.dcide.overview(),
                },
            },
        },
        productsItem,
        SERVICES_ITEM(industryCategories),
        // {
        //     title: 'DC Catalog',
        //     highlightPatterns: [/^\/(products\/[^/]+)/],
        //     href: ComponentHelpers.urls.search(),
        //     icon: <BsBox size={16} />,
        //     items: [
        //         {
        //             icon: <BsBox size={16} />,
        //             title: 'DC Products',
        //             href: ComponentHelpers.urls.dcProducts(),
        //             description: 'Discover a curated selection of DC products',
        //         },
        //         {
        //             icon: <IoBriefcaseOutline size={16} />,
        //             title: 'DC Manufacturers',
        //             href: CompanyHelpers.urls.dcManufacturers(),
        //             description: 'Discover DC product manufacturers',
        //         },
        //         {
        //             icon: <IoAlbumsOutline size={16} />,
        //             title: 'DC Reference Designs',
        //             href: RouterHelpers.urls.designs.search(),
        //             description: 'Real-world examples to inspire your next project',
        //         },
        //     ],
        //     // when re-enabling, swap the bottomSection for users with a profile like useProductsItem does
        //     bottomSection: ADD_PRODUCT_BOTTOM_SECTION(),
        // },
        {
            title: 'Microgrid Controller',
            href: MarketingHelpers.urls.controller.overview(),
            icon: <IoHardwareChipOutline size={16} />,
            items: [
                {
                    icon: <IoHardwareChipOutline size={16} />,
                    title: 'DCIDE Controller',
                    description: 'Versatile, Open, Python programmable, Multi-vendor, Reliable',
                    href: MarketingHelpers.urls.controller.overview(),
                    categories: [
                        {
                            title: 'Features',
                            subcategories: [
                                {
                                    title: 'Core Integration Features',
                                    href: MarketingHelpers.urls.controller.coreIntegrationFeatures(),
                                },
                                {
                                    title: 'Intelligent Energy Control',
                                    href: MarketingHelpers.urls.controller.intelligentEnergyControl(),
                                },
                                {
                                    title: 'Custom Application Development',
                                    href: MarketingHelpers.urls.controller.customApplicationDevelopment(),
                                },
                            ],
                        },
                        {
                            title: 'Pricing',
                            subcategories: [
                                {
                                    title: 'Pricing Overview',
                                    href: MarketingHelpers.urls.controller.pricing(),
                                },
                                {
                                    title: 'Configure Your Solution',
                                    href: MarketingHelpers.urls.controller.pricingConfigurator(),
                                },
                            ],
                        },
                        {
                            title: 'Resources',
                            subcategories: [
                                {
                                    title: 'FAQ',
                                    href: MarketingHelpers.urls.controller.faq(),
                                },
                                {
                                    title: 'Case Studies',
                                    href: MarketingHelpers.urls.controller.caseStudies(),
                                },
                                {
                                    title: 'Documentation',
                                    href: MarketingHelpers.urls.controller.documentation(),
                                },
                                {
                                    icon: <TbHeartHandshake size={15} strokeWidth={1.8} />,
                                    title: 'Partner Program',
                                    href: MarketingHelpers.urls.controller.partnerProgram(),
                                    highlighted: true,
                                },
                            ],
                        },
                    ],
                },
            ],
            bottomSection: {
                title: 'No time to waste. Start developing today.',
                description: 'Transparent Pricing. Guaranteed Engineering Support.',
                button: {
                    label: 'Configure Your Solution',
                    href: MarketingHelpers.urls.controller.pricingConfigurator(),
                },
            },
        },
    ];
};

const useResourceProNavItems = (): NavItem[] => {
    const { industryCategories } = useIndustryCategories();
    const productsItem = useProductsItem();

    return [
        {
            icon: <ReplusLogo w={16} />,
            title: 'About',
            href: MarketingHelpers.urls.resourcepro.overview(),
            highlightPatterns: [/^\/resourcepro$/],
        },
        productsItem,
        SERVICES_ITEM(industryCategories),
        {
            icon: <IoCalendarOutline size={16} />,
            href: MarketingHelpers.urls.resourcepro.resources(),
            title: 'Resources',
            highlightPatterns: [
                /^\/(events\/[^/]+)/,
                /^\/(designs\/[^/]+)/,
                /^\/(case-studies\/[^/]+)/,
                /^\/resourcepro\/resources$/,
            ],
            items: [
                {
                    icon: <IoCalendarOutline size={16} />,
                    title: 'Upcoming RE+ Events',
                    href: RouterHelpers.urls.events.upcoming(),
                },
                {
                    icon: <IoBriefcaseOutline size={16} />,
                    title: 'Microgrid Case Studies',
                    href: RouterHelpers.urls.caseStudies(),
                },
                {
                    icon: <BsPencil size={16} />,
                    title: 'Microgrid Reference Designs',
                    href: RouterHelpers.urls.designs.overview(),
                },
                {
                    icon: <IoMapOutline size={16} />,
                    title: 'DC Microgrid Map',
                    href: RouterHelpers.urls.map(),
                    categories: [
                        {
                            title: 'Company Profile Resources',
                            subcategories: [
                                {
                                    title: 'QR Codes at RE+ Events',
                                    href: MarketingHelpers.urls.resourcepro.qrCodes(),
                                },
                                {
                                    title: 'Media Toolkit',
                                    href: MarketingHelpers.urls.resourcepro.mediaToolkit(),
                                },
                                {
                                    title: 'RE+Source PRO Insider',
                                    href: '/resourcepro/insider',
                                },

                                {
                                    title: 'Contact RE+Source PRO Support',
                                    href: 'mailto:support@directenergypartners.com',
                                    external: true,
                                },
                            ],
                        },
                    ],
                },
            ],
            bottomSection: {
                title: 'Want More Exposure? ',
                button: {
                    label: 'Discover Upgrade Options',
                    href: MarketingHelpers.urls.resourcepro.pricing(),
                },
            },
        },
        {
            icon: <IoHelpCircleOutline size={16} />,
            title: 'FAQs',
            href: MarketingHelpers.urls.resourcepro.faq(),
        },
    ];
};
